import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { to: "/", label: "首页", en: "HOME" },
  { to: "/category", label: "影片分类", en: "CATEGORY" },
  { to: "/search", label: "搜索", en: "SEARCH" },
  { to: "/about", label: "关于我们", en: "ABOUT" },
];

export default function SiteNav() {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/90 backdrop-blur-xl">
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="group flex shrink-0 items-center gap-3" aria-label="韩国三级电影 首页">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-film-line text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="whitespace-nowrap font-heading text-base font-bold tracking-tight text-foreground-950 md:text-lg">
              韩国三级电影
            </span>
            <span className="mt-1 hidden whitespace-nowrap font-label text-[10px] uppercase tracking-[0.28em] text-foreground-500 sm:block">
              Korean Film Archive
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navLinks.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `group relative rounded-md px-4 py-2 text-sm transition-colors ${
                  isActive
                    ? "text-foreground-950"
                    : "text-foreground-500 hover:text-foreground-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="whitespace-nowrap">{item.label}</span>
                  <span
                    className={`absolute inset-x-4 -bottom-0.5 h-0.5 rounded-full transition-all ${
                      isActive ? "bg-accent-500" : "bg-transparent group-hover:bg-background-400"
                    }`}
                  ></span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className="hidden items-center gap-2 rounded-md border border-background-200 bg-background-100/80 px-3 py-2 transition-colors focus-within:border-primary-400 md:flex"
            role="search"
          >
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片 / 导演 / 演员"
              aria-label="搜索影片"
              className="w-36 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500 xl:w-48"
            />
          </form>

          <Link
            to="/category?c=hot"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            热门榜单
          </Link>

          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            aria-label="打开菜单"
            aria-expanded={open}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-900 transition-colors hover:bg-background-100 lg:hidden"
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-3-line text-xl"}></i>
          </button>
        </div>
      </div>

      {open && (
        <div className="animate-slide-down border-t border-background-200/70 bg-background-50 px-4 pb-5 pt-3 lg:hidden">
          <form onSubmit={handleSearch} className="mb-3 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片 / 导演 / 演员"
              aria-label="搜索影片"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
            <button type="submit" className="whitespace-nowrap text-sm text-accent-500">
              搜索
            </button>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md border px-3 py-3 text-sm transition-colors ${
                    isActive
                      ? "border-primary-400 bg-primary-50 text-foreground-950"
                      : "border-background-200 bg-background-100 text-foreground-700"
                  }`
                }
              >
                <span>{item.label}</span>
                <span className="font-label text-[10px] uppercase tracking-widest text-foreground-500">
                  {item.en}
                </span>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}